import { createContext, useState, useEffect, useContext } from 'react';
import api, { endpoints } from '../services/api';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);

  // Verificar token al cargar
  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get(endpoints.auth.me);
      const userData = response.data.data;
      setUser(userData);
      setPermissions(userData.permissions || []);
    } catch (error) {
      console.error('Error al obtener usuario:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post(endpoints.auth.login, {
        email,
        password
      });

      const { token: newToken, user: userData } = response.data.data;
      
      setToken(newToken);
      setUser(userData);
      setPermissions(userData.permissions || []);
      localStorage.setItem('token', newToken);
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al iniciar sesión'
      };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setPermissions([]);
    localStorage.removeItem('token');
  };

  const refreshToken = async () => {
    try {
      const response = await api.post(endpoints.auth.refresh);
      const { token: newToken } = response.data.data;
      
      setToken(newToken);
      localStorage.setItem('token', newToken);
      
      return true;
    } catch (error) {
      logout();
      return false;
    }
  };

  // Verificar si tiene un permiso específico
  const hasPermission = (permissionCode) => {
    if (!user || !permissions) return false;
    return permissions.includes(permissionCode);
  };

  // Verificar acceso a módulo (verifica módulo padre)
  const hasModuleAccess = (module) => {
    return hasPermission(module);
  };

  // Verificar si el usuario es admin (basado en permisos)
  const isAdmin = hasModuleAccess('admin');

  const value = {
    user,
    token,
    loading,
    permissions,
    login,
    logout,
    refreshToken,
    isAuthenticated: !!token,
    isAdmin,
    hasPermission,
    hasModuleAccess
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook personalizado para usar el contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de AuthProvider');
  }
  return context;
};
